import { useCallback, useEffect, useMemo } from 'react';
import { LandingPageTemplateV1 } from '@frontend/api-landing-page-template';
import { ReviewSitesV1 } from '@frontend/api-review-sites';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { DropdownField, FieldProps, useAlert } from '@frontend/design-system';
import { AffiliateReviewIds, AffiliateReviewType } from '../../types';

interface ReviewLinkStepProps {
  fieldProps: FieldProps<any, 'reviewLink'>;
  templateLocations: string[];
}

export const ReviewLinkStep = ({ fieldProps, templateLocations }: ReviewLinkStepProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('templates-settings');

  const alert = useAlert();

  const { data: landingPageTemplates, isLoading: isLandingLoading } =
    LandingPageTemplateV1.Queries.useListLandingPageTemplatesQuery(
      { groupIds: selectedLocationIds },
      {
        select: (data) =>
          data?.templates?.map((template) => ({
            title: template.title,
            name: template.templateId,
            templateId: template.templateId,
          })) ?? [],
      }
    );

  const {
    data: socialMediaSettings,
    isLoading: isSettingsLoading,
    isError: isSocialMediaSettingsError,
  } = ReviewSitesV1.Queries.useGetReviewsSettingsQuery({
    groupIds: templateLocations?.length ? templateLocations : selectedLocationIds,
  });

  const isLoading = isLandingLoading || isSettingsLoading;

  const affiliateReviewTemplates = useMemo(() => {
    if (isSocialMediaSettingsError || !socialMediaSettings) return [];
    const googleUrl = socialMediaSettings?.googleUrl;
    const facebookUrl = socialMediaSettings?.facebookUrl;
    return [
      googleUrl && {
        templateId: AffiliateReviewIds.GOOGLE,
        name: AffiliateReviewIds.GOOGLE,
        title: AffiliateReviewType.GOOGLE,
      },
      facebookUrl && {
        templateId: AffiliateReviewIds.FACEBOOK,
        name: AffiliateReviewIds.FACEBOOK,
        title: AffiliateReviewType.FACEBOOK,
      },
    ].filter((data) => !!data);
  }, [socialMediaSettings, isSocialMediaSettingsError]);

  const showAffiliateWarnings = useCallback(() => {
    if (isLoading || isSocialMediaSettingsError) return;
    if (!affiliateReviewTemplates) return;

    const isSingleLocationSelected = templateLocations?.length === 1;
    const locationWord = isSingleLocationSelected ? 'location' : 'locations';

    const hasGoogle = affiliateReviewTemplates.some((item) => item.templateId === AffiliateReviewIds.GOOGLE);

    const hasFacebook = affiliateReviewTemplates.some((item) => item.templateId === AffiliateReviewIds.FACEBOOK);

    if (!hasGoogle && !hasFacebook) {
      alert.warning(
        t(
          `No integration found for Google or Facebook at the selected ${locationWord}. 
  Please use the Private or Custom options to collect reviews.`
        )
      );
      return;
    }

    if (!hasGoogle) {
      alert.warning(
        t(
          `No integration found for Google at the selected ${locationWord}. 
  Please use the Private or Custom options to collect reviews. 
  Avoid using review links with Google review button to collect reviews.`
        )
      );
      return;
    }

    if (!hasFacebook) {
      alert.warning(
        t(
          `No integration found for Facebook at the selected ${locationWord}. 
  Please use the Private or Custom options to collect reviews. 
  Avoid using review links with Facebook review button to collect reviews.`
        )
      );
    }
  }, [affiliateReviewTemplates, isLoading, isSocialMediaSettingsError, templateLocations]);

  useEffect(() => {
    showAffiliateWarnings();
  }, [showAffiliateWarnings]);

  const combinedTemplates = useMemo(() => {
    return [...(landingPageTemplates ?? []), ...affiliateReviewTemplates];
  }, [landingPageTemplates, affiliateReviewTemplates]);

  const { onChange, value, ...rest } = fieldProps;

  const handleChange = (selectedTemplateId: string) => {
    onChange({
      target: {
        name: 'reviewLink',
        value: selectedTemplateId,
      },
    });
  };
  return (
    <DropdownField {...rest} label='Review Link' value={value ?? ''} onChange={(e) => handleChange(e.value)}>
      {!isLoading &&
        combinedTemplates.map((link) => (
          <DropdownField.Option key={link.templateId} value={link?.templateId || ''}>
            {link.title}
          </DropdownField.Option>
        ))}
    </DropdownField>
  );
};
